import { MapColorThemeContextProps } from "@app/shared/map-appearance/interfaces";
import { MapColorThemeSwatch } from "@app/shared/map-appearance/components/MapColorThemeSwatch";
import { MapColorSlotEditor } from "@app/shared/map-appearance/components/MapColorSlotEditor";
import { Modal, ModalContent } from "@app/shared/Modals";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { Context, FC, useContext, useEffect, useMemo, useState } from "react";

interface MapColorThemeEditFlyoutProps {
  colorThemeContext: Context<MapColorThemeContextProps | null>;
  onDirtyChange?: (dirty: boolean) => void;
  warningModal: Omit<ModalContent, "confirmButtonAction">;
}

export const MapColorThemeEditFlyout: FC<MapColorThemeEditFlyoutProps> = ({
  colorThemeContext,
  onDirtyChange,
  warningModal,
}) => {
  const ctx = useContext(colorThemeContext);
  if (!ctx) throw new Error("MapColorThemeEditFlyout must be used within a MapColorThemeContextProvider");

  const [initialTheme, setInitialTheme] = useState(ctx.themeToEdit);
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const isDuplicateCustomName = useMemo(
    () =>
      !!ctx.customThemes.find(
        s => s.name.toLowerCase() === ctx.themeToEdit?.name.toLowerCase() && s.uuid !== ctx.themeToEdit?.uuid
      ),
    [ctx.customThemes, ctx.themeToEdit]
  );

  const isDuplicatePresetName = useMemo(
    () => !!ctx.presetThemes.find(s => s.name.toLowerCase() === ctx.themeToEdit?.name.toLowerCase()),
    [ctx.presetThemes, ctx.themeToEdit]
  );

  useEffect(() => {
    if (ctx.themeToEdit && !initialTheme) setInitialTheme(ctx.themeToEdit);
    if (!ctx.themeToEdit) {
      setInitialTheme(null);
      setIsDirty(false);
    }
  }, [ctx.themeToEdit]);

  useEffect(() => {
    if (ctx.themeToEdit && initialTheme) {
      setIsDirty(JSON.stringify(ctx.themeToEdit) !== JSON.stringify(initialTheme));
    }
  }, [ctx.themeToEdit, initialTheme]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const isNameValid = !!ctx.themeToEdit?.name && !isDuplicateCustomName && !isDuplicatePresetName;

  const nameDescriptiveText = (() => {
    if (!ctx.themeToEdit?.name) return "Please provide a theme name.";
    if (isDuplicateCustomName) return "Theme name already exists. Please choose a different name.";
    if (isDuplicatePresetName) return "This name is reserved for a system theme. Please choose a different name.";
    return "";
  })();

  const handleSave = () => {
    ctx.addOrUpdateCustomTheme(ctx.themeToEdit!);
    ctx.setThemeToEdit(null);
    ctx.setEditingSlotIndex(null);
  };

  const handleCancel = () => {
    if (isDirty) {
      setIsCancelModalVisible(true);
    } else {
      ctx.setThemeToEdit(null);
      ctx.setEditingSlotIndex(null);
    }
  };

  return (
    <>
      <div>
        <p><strong>Custom Color Themes</strong></p>
        {ctx.customThemes.length === 0 && (
          <QESAlert persist={true} type="info">
            No custom color themes available.
          </QESAlert>
        )}
        {ctx.customThemes.map(theme => (
          <MapColorThemeSwatch
            colorThemeContext={colorThemeContext}
            key={theme.uuid}
            theme={theme}
          />
        ))}
        {!ctx.themeToEdit && (
          <QESButton
            onClick={() => {
              ctx.setThemeToEdit(ctx.createDefaultTheme());
              ctx.setEditingSlotIndex(null);
            }}
            qesTestId="map-appearance__create-new-theme"
            qesType="secondary"
          >
            Create new theme
          </QESButton>
        )}
        {!!ctx.themeToEdit && (
          <>
            <QESTextInput
              containerClassName="map-appearance__theme-name-input"
              descriptiveText={nameDescriptiveText}
              isValid={isNameValid}
              label="Theme Name:"
              maxLength={25}
              onChange={(e) => ctx.setThemeToEdit({ ...ctx.themeToEdit!, name: e.target.value })}
              placeholder="Enter theme name"
              required={true}
              testId="map-appearance__theme-name-input"
              value={ctx.themeToEdit.name}
            />
            {ctx.slotDefinitions.map((_, index) => (
              <MapColorSlotEditor
                colorThemeContext={colorThemeContext}
                key={index}
                slotIndex={index}
              />
            ))}
            <QESButton
              disabled={!isNameValid}
              onClick={handleSave}
              qesTestId="map-appearance__save-theme"
              qesType="secondary"
            >
              Save
            </QESButton>
            <QESButton
              onClick={handleCancel}
              qesTestId="map-appearance__cancel-theme"
              qesType="secondary-danger"
            >
              Cancel
            </QESButton>
          </>
        )}
      </div>
      <Modal
        closeModal={() => setIsCancelModalVisible(false)}
        content={{
          body: warningModal.body,
          cancelButtonText: warningModal.cancelButtonText,
          confirmButtonAction: () => {
            ctx.setThemeToEdit(null);
            ctx.setEditingSlotIndex(null);
            setIsCancelModalVisible(false);
          },
          confirmButtonText: warningModal.confirmButtonText,
          title: warningModal.title,
        }}
        isVisible={isCancelModalVisible}
      />
    </>
  );
};
